/**
 * UpdateNotice — "pidly has been updated. Reload."
 *
 * The app is one bundle, and an open tab keeps running the bundle it
 * loaded until the page is reloaded: nothing in the SPA ever fetches new
 * code on its own. So a tab left open across a deploy shows the OLD app
 * indefinitely, and "nothing has changed" after a push is usually this.
 *
 * On load, whenever the tab regains focus, and every ten minutes, the
 * page's own HTML is fetched (no-store, so never a cached copy) and the
 * bundle it names is compared with the bundle that is running. A
 * different name means a newer build is live, and every notification bell
 * shows a "pidly has been updated" item with a Reload button (see
 * utils/updateAvailable + components/UpdateNotifItem). It never reloads on its own — a form half-typed or a markup
 * half-drawn is the user's.
 */
import { useEffect } from 'react';
import { setUpdateAvailable, runningBundle, liveBundle } from '../utils/updateAvailable';

const CHECK_EVERY_MS = 10 * 60 * 1000;

export default function UpdateNotice() {

  useEffect(() => {
    if (import.meta.env.DEV) return undefined;
    const mine = runningBundle();
    if (!mine) return undefined;
    let stopped = false;
    const check = async () => {
      try {
        const live = await liveBundle();
        if (!stopped && live && live !== mine) setUpdateAvailable(true);
      } catch { /* offline, or the host is mid-deploy: try again later */ }
    };
    check();
    const timer = setInterval(check, CHECK_EVERY_MS);
    const onFocus = () => { if (document.visibilityState === 'visible') check(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => { stopped = true; clearInterval(timer); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
  }, []);

  // Rendered as an item in the notification bells, not a bar of its own.
  return null;
}
